import React, { useState, useEffect, useMemo } from 'react';
import { ProtocolFilters as ProtocolFiltersType } from '../../../types/protocols';
import './ProtocolFilters.scss';
import CustomSelect from '../../../components/CustomSelect';

interface ProtocolFiltersProps {
  filters: ProtocolFiltersType;
  onFiltersChange: (filters: Partial<ProtocolFiltersType>) => void;
  onReset: () => void;
  specialties: string[];
}

const ProtocolFilters: React.FC<ProtocolFiltersProps> = ({
  filters,
  onFiltersChange,
  onReset,
  specialties,
}) => {
  const [searchQuery, setSearchQuery] = useState(filters.search || '');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  // Синхронизируем searchQuery с внешними изменениями filters.search
  useEffect(() => {
    if (filters.search !== searchQuery) {
      setSearchQuery(filters.search || '');
    }
  }, [filters.search, searchQuery]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Update search filter when debounced query changes
  useEffect(() => {
    // Избегаем ненужных обновлений если значение не изменилось
    const newSearchValue = debouncedSearchQuery || undefined;
    if (filters.search !== newSearchValue) {
      onFiltersChange({ search: newSearchValue });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery]); // Убираем onFiltersChange из зависимостей чтобы избежать бесконечного цикла

  const handleSpecialtyChange = (specialty: string) => {
    const newSpecialty = specialty === 'all' ? undefined : specialty;
    onFiltersChange({ specialty: newSpecialty });
  };

  const handleStatusChange = (status: 'all' | 'active' | 'inactive') => {
    onFiltersChange({ status });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleReset = () => {
    setSearchQuery('');
    onReset();
  };

  const hasActiveFilters = filters.specialty || filters.status !== 'all' || filters.search;

  // Мемоизируем options для избежания пересоздания
  const specialtyOptions = useMemo(() => [
    { value: "all", label: "Все специальности" },
    ...specialties.map((specialty) => ({
      value: specialty,
      label: specialty
    }))
  ], [specialties]);

  const statusOptions = useMemo(() => [
    { value: "all", label: "Все статусы" },
    { value: "active", label: "Только активные" },
    { value: "inactive", label: "Только неактивные" }
  ], []);

  return (
    <div className="protocol-filters">
      <h2>Управление шаблонами</h2>
      
      <div className="protocols-controls">
        <div className="search-filters">
          <input
            type="text"
            placeholder="Поиск по названию или описанию..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="search-input"
          />
          
          <CustomSelect
            value={filters.specialty || 'all'}
            onChange={(value) => handleSpecialtyChange(value.toString())}
            className=""
            placeholder="Все специальности"
            options={specialtyOptions}
          />

          <CustomSelect
            value={filters.status || 'all'}
            onChange={(value) => handleStatusChange(value as 'all' | 'active' | 'inactive')}
            className=""
            placeholder="Все статусы"
            options={statusOptions}
          />
        </div>

        {hasActiveFilters && (
          <button
            className="btn btn-link btn-sm reset-filters-btn"
            onClick={handleReset}
          >
            Сбросить фильтры
          </button>
        )}
      </div>

      {hasActiveFilters && (
        <div className="protocol-filters__active">
          <span className="active-filters-label">Активные фильтры:</span>
          <div className="active-filters-tags">
            {filters.specialty && (
              <span className="filter-tag">
                Специальность: {filters.specialty}
                <button
                  className="filter-tag__remove"
                  onClick={() => onFiltersChange({ specialty: undefined })}
                >
                  ×
                </button>
              </span>
            )}
            {filters.status !== 'all' && (
              <span className="filter-tag">
                Статус: {filters.status === 'active' ? 'Активные' : 'Неактивные'}
                <button
                  className="filter-tag__remove"
                  onClick={() => onFiltersChange({ status: 'all' })}
                >
                  ×
                </button>
              </span>
            )}
            {filters.search && (
              <span className="filter-tag">
                Поиск: "{filters.search}"
                <button
                  className="filter-tag__remove"
                  onClick={() => {
                    setSearchQuery('');
                    onFiltersChange({ search: undefined });
                  }}
                >
                  ×
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProtocolFilters;

